import React from 'react';
import { RequestStatus } from '../types';
import { useTranslation } from '../context/TranslationContext';
import '../styles/StatusBadge.css';

interface StatusBadgeProps {
  status: RequestStatus;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const { t } = useTranslation();
  const getStatusConfig = (status: RequestStatus) => {
    switch (status) {
      // Legacy statuses
      case 'Prep':
        return { color: '#ef4444', progress: 0, bgColor: '#fef2f2' };
      case 'RFQ Running':
        return { color: '#ef4444', progress: 40, bgColor: '#fef2f2' };
      case 'Negotiation':
        return { color: '#3b82f6', progress: 80, bgColor: '#eff6ff' };
      case 'Delivered':
        return { color: '#10b981', progress: 100, bgColor: '#f0fdf4' };
      
      // New RFQ phases
      case 'document_preparation':
        return { color: '#3b82f6', progress: 16, bgColor: '#eff6ff' };
      case 'define_supplier_list':
        return { color: '#3b82f6', progress: 33, bgColor: '#eff6ff' };
      case 'send_out_rfq':
        return { color: '#f59e0b', progress: 50, bgColor: '#fffbeb' };
      case 'receive_responses':
        return { color: '#f59e0b', progress: 66, bgColor: '#fffbeb' };
      case 'select_supplier':
        return { color: '#10b981', progress: 83, bgColor: '#f0fdf4' };
      case 'advise_unsuccessful_suppliers':
        return { color: '#10b981', progress: 100, bgColor: '#f0fdf4' };
      
      // Special statuses
      case 'revision_required':
        return { color: '#ef4444', progress: 100, bgColor: '#fef2f2' };
      case 'completed':
        return { color: '#059669', progress: 100, bgColor: '#ecfdf5' };
      case 'pending_admin_approval':
        return { color: '#3b82f6', progress: 25, bgColor: '#eff6ff' };
      
      // Status aliases
      case 'approved':
        return { color: '#10b981', progress: 83, bgColor: '#f0fdf4' };
      
      default:
        return { color: '#6b7280', progress: 0, bgColor: '#f9fafb' };
    }
  };

  const config = getStatusConfig(status);

  return (
    <div className="status-badge">
      <div className="status-text">
        {/* Legacy statuses */}
        {status === 'Prep' && t('common.prep')}
        {status === 'RFQ Running' && t('common.rfq_running')}
        {status === 'Negotiation' && t('common.negotiation')}
        {status === 'Delivered' && t('common.delivered')}
        
        {/* New RFQ phases */}
        {status === 'document_preparation' && t('common.document_preparation')}
        {status === 'define_supplier_list' && t('common.define_supplier_list')}
        {status === 'send_out_rfq' && t('common.send_out_rfq')}
        {status === 'receive_responses' && t('common.receive_responses')}
        {status === 'select_supplier' && t('common.select_supplier')}
        {status === 'advise_unsuccessful_suppliers' && t('common.advise_unsuccessful_suppliers')}
        
        {/* Special statuses */}
        {status === 'revision_required' && t('common.revision_required')}
        {status === 'completed' && t('common.completed')}
        {status === 'pending_admin_approval' && t('common.pending_admin_approval')}
        
        {/* Status aliases */}
        {status === 'approved' && t('common.select_supplier')}
        
        {/* Fallback for unknown statuses */}
        {!['Prep', 'RFQ Running', 'Negotiation', 'Delivered', 'approved', 'document_preparation', 'define_supplier_list', 'send_out_rfq', 'receive_responses', 'select_supplier', 'advise_unsuccessful_suppliers', 'revision_required', 'completed', 'pending_admin_approval'].includes(status) && status}
      </div>
      
      {/* Progress bar for all phases except Prep */}
      {status !== 'Prep' && (
        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ 
              width: `${config.progress}%`,
              backgroundColor: config.color
            }}
          />
        </div>
      )}
      
      {/* Dot for Prep status */}
      {status === 'Prep' && (
        <div className="prep-dot" style={{ backgroundColor: config.color }} />
      )}
    </div>
  );
};

export default StatusBadge; 